import {
    ICSVData,
    IDetectHasHeadersOptions,
    IHeaderOption,
    IMatchHeadersOptions,
    IParseCSVOptions,
} from 'common/types';

/**
 * Detects whether a CSV line uses commas or semicolons as delimiter
 * by comparing their frequency. Defaults to comma when equal.
 */
function detectDelimiter(headerLine: string): string {
    const commaCount = (headerLine.match(/,/g) || []).length;
    const semicolonCount = (headerLine.match(/;/g) || []).length;

    return semicolonCount > commaCount ? ';' : ',';
}

/**
 * Parses a single CSV line into an array of trimmed field values.
 * Handles quoted fields and escaped double-quotes (e.g. `""` becomes `"`).
 */
function parseLine(line: string, delimiter: string): string[] {
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;

    for (let i = 0; i < line.length; i++) {
        const char = line[i];
        const nextChar = line[i + 1];

        if (inQuotes) {
            if (char === '"' && nextChar === '"') {
                current += '"';
                i++;
            } else if (char === '"') {
                inQuotes = false;
            } else {
                current += char;
            }
        } else if (char === '"') {
            inQuotes = true;
        } else if (char === delimiter) {
            fields.push(current.trim());
            current = '';
        } else {
            current += char;
        }
    }

    fields.push(current.trim());

    return fields;
}

/**
 * Heuristically determines whether the first row of a CSV is a header row.
 * Uses known header names and data patterns (e.g. email regex) to distinguish
 * headers from data. Defaults to `true` when undetermined.
 */
function detectHasHeaders(
    firstRow: string[],
    dataRows: string[][],
    options?: IDetectHasHeadersOptions
): boolean {
    const knownHeaders = options?.knownHeaders;
    const dataPattern = options?.dataPattern;
    const normalized = firstRow.map((v) => v.toLowerCase().trim());

    // If any first-row value matches a known header name, likely has headers
    if (knownHeaders) {
        const knownHeaderMatches = normalized.filter((v) =>
            knownHeaders.has(v)
        ).length;

        if (knownHeaderMatches > 0) return true;
    }

    if (dataPattern) {
        // If first row contains data pattern matches, it's likely data, not headers
        const firstRowHasDataPattern = firstRow.some((v) =>
            dataPattern.test(v.trim())
        );

        if (firstRowHasDataPattern) return false;

        // Compare first row against data rows: headers tend to differ in pattern
        // If data rows contain pattern matches but first row doesn't, likely headers
        const dataHasPattern = dataRows.some((row) =>
            row.some((v) => dataPattern.test(v.trim()))
        );

        if (dataHasPattern) return true;
    }

    // Default: assume headers are present
    return true;
}

export const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

/**
 * Matches CSV header names to header options using case-insensitive
 * comparison against option labels and known aliases.
 * Each option can only be matched once to prevent duplicate column assignments.
 */
export function matchHeadersToOptions<T>(
    headers: string[],
    options: IHeaderOption<T>[],
    { aliases, ignoreValue }: IMatchHeadersOptions<T>
): T[] {
    const used = new Set<T>();

    return headers.map((header) => {
        const normalized = header.toLowerCase().trim();

        // Try matching against option labels (case-insensitive)
        const labelMatch = options.find(
            (opt) =>
                opt.value !== ignoreValue &&
                !used.has(opt.value) &&
                opt.label.toLowerCase().trim() === normalized
        );

        if (labelMatch) {
            used.add(labelMatch.value);

            return labelMatch.value;
        }

        // Try matching against known header name aliases
        if (aliases) {
            const aliasMatch = aliases[normalized];

            if (aliasMatch && !used.has(aliasMatch)) {
                used.add(aliasMatch);

                return aliasMatch;
            }
        }

        return ignoreValue;
    });
}

/**
 * Parses a raw CSV string into structured data. Auto-detects the delimiter
 * (comma or semicolon) and whether the first row contains headers.
 */
export function parseCSV(
    content: string,
    options?: IParseCSVOptions
): ICSVData {
    const normalized = content.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    const lines = normalized
        .split('\n')
        .filter((line) => line.trim().length > 0);

    if (lines.length === 0) {
        return { headers: [], rows: [], hasHeaders: false };
    }

    const delimiter = detectDelimiter(lines[0]);
    const headers = parseLine(lines[0], delimiter);
    const rows = lines.slice(1).map((line) => parseLine(line, delimiter));
    const hasHeaders = detectHasHeaders(headers, rows, options);

    return { headers, rows, hasHeaders };
}
